import { isSuperUser } from '@utils/permissions';
import { FileUploader } from 'react-drag-drop-files';
import { useSelector } from 'react-redux';

import type { RootState } from '@features/redux/store';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import csvtojson from 'csvtojson';
import { useAlert } from '@hooks/useAlert';
import { useState } from 'react';
import { fetchModifyCuts } from '@api/reporting';

/**
 * Component allows for user to upload CSV file with 3 columns:
 * 1. Cut ID
 * 2. Date
 * 3. Time
 *
 * This CSV will be sent to the api to update cut dates, only superusers can access view this component.
 * If not a superuser, this component will not render.
 * SuperUser = all permissions available
 */
export const CutDateUpload = () => {
    const { IdToken } = useSelector((state: RootState) => state.auth);

    const [loading, setLoading] = useState(false);

    const { setAlert } = useAlert();

    if (!isSuperUser(IdToken!)) {
        return null;
    }

    const handleUpload = async (file: File) => {
        setLoading(true);
        const text = await file.text();
        const json = await csvtojson().fromString(text);
        const isValid = json.every((row) => row.CutID && row.Date && row.Time);
        if (!isValid) {
            setAlert('Invalid CSV! CSV must have 3 columns - CutID, Date, Time', 'error');
            setLoading(false);
            return;
        }
        const { success, numRows } = await fetchModifyCuts(json, IdToken!);
        if (success) {
            setAlert(`Successfully updated ${numRows} cut dates!`, 'success');
        } else {
            setAlert('Failed to update cut dates!', 'error');
        }
        setLoading(false);
    };

    return (
        <Box display={'flex'} flexDirection={'row'} flex={1} justifyContent={'space-between'} pr={'20px'} pl={'20px'}>
            <FileUploader
                handleChange={handleUpload}
                types={['csv']}
                name='file'
                hoverTitle={'Drop here to upload CSV'}>
                <div
                    style={{
                        padding: '20px',
                        borderStyle: 'dashed',
                        borderColor: 'blue',
                        borderWidth: '2px',
                        justifyContent: 'center',
                        width: '400px',
                        height: '50px',
                        alignContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        display: 'flex',
                        flex: 1,
                    }}>
                    {loading ? (
                        <CircularProgress />
                    ) : (
                        <>
                            <Typography>
                                <b>Cut Date Modifier:</b> Drop or{' '}
                                <b>
                                    <u>click here</u>
                                </b>{' '}
                                to upload CSV.
                            </Typography>

                            <Typography>
                                <br />
                                <i>
                                    <b>Note:</b> CSV must have 3 columns - CutID, Date, Time
                                </i>
                            </Typography>
                        </>
                    )}
                </div>
            </FileUploader>

            <Button
                variant={'contained'}
                sx={{ textTransform: 'none', height: '50px' }}
                onClick={() => {
                    const csvString = 'CutID,Date,Time\n';
                    const blob = new Blob([csvString], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'cut-date-template.csv';
                    a.click();
                    URL.revokeObjectURL(url); // Clean up memory
                }}>
                Download CSV Template
            </Button>
        </Box>
    );
};
